import { useState, useEffect, useCallback } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { getUserProfile, updateUserProfile } from '../services/apiService';
import { useUserStatus } from '../context/UserStatusContext';
import ConnectedAccounts from '../components/settings/ConnectedAccounts';
import styles from './AccountSettingsPage.module.css';

function AccountSettingsPage() {
  const { user, getAccessTokenSilently } = useAuth0();
  const { userStatus } = useUserStatus();

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    country: '',
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const accessToken = await getAccessTokenSilently();
      const profile = await getUserProfile(accessToken);

      setProfileData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        dateOfBirth: profile.dateOfBirth || '',
        gender: profile.gender || '',
        country: profile.country || '',
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage('');
    try {
      const accessToken = await getAccessTokenSilently();
      await updateUserProfile(profileData, accessToken);
      setSuccessMessage('Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  const canSave =
    profileData.firstName.trim() !== '' &&
    profileData.lastName.trim() !== '' &&
    profileData.gender.trim() !== '' &&
    profileData.dateOfBirth.trim() !== '';

  if (isLoading) return <p>Loading account settings...</p>;

  return (
    <div>
      <div className="page-header">
        <h2>Account Info</h2>
      </div>
      {error && <p className="errorText">Could not load settings: {error}</p>}

      {!error && (
        <div className={styles.settingsGrid}>
          <div className={styles.settingsCard}>
            <h3>Account & Plan</h3>
            <p className={styles.cardDescription}>
              Your current subscription status and usage details.
            </p>
            {userStatus && (
              <div className={styles.statusList}>
                <div className={styles.statusItem}>
                  <span>Email</span>
                  <strong>{user.email}</strong>
                </div>
                <div className={styles.statusItem}>
                  <span>Plan Tier</span>
                  <strong>
                    {userStatus.planTier?.charAt(0).toUpperCase() +
                      userStatus.planTier?.slice(1)}
                  </strong>
                </div>
                <div className={styles.statusItem}>
                  <span>Ego Credits</span>
                  <strong>⚡️ {userStatus.creatorCredits}</strong>
                </div>
                <div className={styles.statusItem}>
                  <span>Monthly Engagement</span>
                  <strong>
                    🔥 {userStatus.monthlyEngagementUsed} /{' '}
                    {userStatus.monthlyEngagementQuota}
                  </strong>
                </div>
              </div>
            )}
          </div>

          <ConnectedAccounts />
        </div>
      )}
    </div>
  );
}

export default withAuthenticationRequired(AccountSettingsPage);
